import { CommonStatusType } from "src/app/globals/custom-types/global-custom-types";
import { Locale } from "../../locale/models/locale";
import { ChartType } from "../enums/chart-type";
import { TrackerAddFormTemplate } from "../modules/tracker-form-templates/enums/tracker-add-form-template";
import Formula, { CalculationType } from "./formula";
import TrackerTag from "./tracker-tag";
import TrackerType from "./tracker-type";
import { UserRole } from "src/app/globals/globals";
import { TrackerDependencyType } from "../enums/tracker-dependency-type";
import Unit from "../../unit/models.ts/unit";
import UnitType from "../../unit-type/models/unit-type";
import { trackerFormTemplateType } from "../modules/tracker-form-templates/types/tracker-form-template-type";
import FoodTrackerFormTemplate from "../modules/tracker-form-templates/models/food-tracker-form-template";
import RangeTrackerFormTemplate from "../modules/tracker-form-templates/models/range-tracker-form-template";
import PushTrackerFormTemplate from "../modules/tracker-form-templates/models/push-tracker-form-template";
import SystemConstants from "src/app/util/constants/SystemConstants";
import { isArray } from "ngx-bootstrap/chronos";

export default class Tracker {
    _id: string;
    calculation: CalculationType;
    chartAttributes: ITrackerChartData;
    currentYearTimeBin: number;
    dependency: IDependencyType;
    description: string;
    image: string;
    isDefaultTracker: boolean;
    isSystemTracker: boolean;
    locale: Partial<Locale>;
    localeList: Locale[] | typeof SystemConstants.ALL;
    creatorId: string;
    shortDescription: string;
    status: CommonStatusType;
    publishEndDate: Date;
    name: string;
    publishStartDate: Date;
    trackerTags: TrackerTag[] | string[];
    trackerType:  Partial<TrackerType> | string;
    trackingRight: TrackingRightUserType;
    unit: Partial<Unit>;
    unitType: Partial<UnitType>;
    updateInterval: number;
    updateIntervalCustomYearsCount?: number;

    constructor(tracker?: Tracker) {
        if(tracker) {
            if(tracker._id)
                this._id = tracker._id
            if(tracker.calculation)
                this.calculation = tracker.calculation
            if(tracker.chartAttributes)
                this.chartAttributes = {...tracker.chartAttributes}
            if(tracker.creatorId)
                this.creatorId = tracker.creatorId
            if(tracker.currentYearTimeBin)
                this.currentYearTimeBin = tracker.currentYearTimeBin
            if(tracker.description)
                this.description = tracker.description
            if(tracker.dependency)
                this.dependency = {...tracker.dependency}
            if(tracker.image)
                this.image = tracker.image
            if (tracker.isDefaultTracker !== undefined && tracker.isDefaultTracker !== null) this.isDefaultTracker = tracker.isDefaultTracker || false
            this.isSystemTracker = tracker.isSystemTracker || false
            if(tracker.locale)
                this.locale = tracker.locale
            if(tracker.localeList && isArray(tracker.localeList) && tracker.localeList.length > 0)
                this.localeList = Object.assign([], tracker.localeList)
            if(tracker.localeList === SystemConstants.ALL)
                this.localeList = SystemConstants.ALL
            if(tracker.name)
                this.name = tracker.name
            if(tracker.publishEndDate)
                this.publishEndDate = new Date(tracker.publishEndDate)
            if(tracker.publishStartDate)
                this.publishStartDate = new Date(tracker.publishStartDate)
            if(tracker.shortDescription)
                this.shortDescription = tracker.shortDescription
            if(tracker.status)
                this.status = tracker.status
            if(tracker.trackerTags)
                this.trackerTags = Object.assign([], tracker.trackerTags)
            if(tracker.trackerType)
                this.trackerType = tracker.trackerType
            if(tracker.trackingRight || tracker.trackingRight === 0)
                this.trackingRight = tracker.trackingRight
            if(tracker.unit)
                this.unit = {...tracker.unit}
            if(tracker.unitType)
                this.unitType = {...tracker.unitType}
            this.updateInterval = Number(tracker.updateInterval)
            if(tracker.updateIntervalCustomYearsCount)
                this.updateIntervalCustomYearsCount = tracker.updateIntervalCustomYearsCount
        }            
    }

    get commonNutrition() {
        if(this.dependency.details instanceof IndependentTracker 
            && this.dependency.details.templateDetails instanceof FoodTrackerFormTemplate 
            && this.dependency.details.templateDetails.commonNutrition) {
                return this.dependency.details.templateDetails.commonNutrition;
            }
    }

    get dependsOnTrackers() {
        if(this.dependency.details instanceof DependentTracker
            && this.dependency.details.dependsOnTrackers)
                return this.dependency.details.dependsOnTrackers;
    }

    get formulaDetails() {
        if(this.dependency.details instanceof DependentTracker
            && this.dependency.details.formula)
                return this.dependency.details.formula;
    }

    get minMaxMoveValues() {
        if(this.dependency.details instanceof IndependentTracker 
            && ((this.dependency.details.templateDetails as PushTrackerFormTemplate).maximum || (this.dependency.details.templateDetails as PushTrackerFormTemplate).maximum === 0)
            && ((this.dependency.details.templateDetails as PushTrackerFormTemplate).minimum || (this.dependency.details.templateDetails as PushTrackerFormTemplate).minimum === 0)
            && ((this.dependency.details.templateDetails as PushTrackerFormTemplate).move || (this.dependency.details.templateDetails as PushTrackerFormTemplate).move === 0)) {
                return { 
                    max: (this.dependency.details.templateDetails as PushTrackerFormTemplate).maximum,
                    min: (this.dependency.details.templateDetails as PushTrackerFormTemplate).minimum,
                    move: (this.dependency.details.templateDetails as PushTrackerFormTemplate).move
            }
        }
    }
    
    get selectedTemplate() {
        if(this.dependency.details instanceof IndependentTracker 
            && this.dependency.details.selectedTemplate) {
                return this.dependency.details.selectedTemplate;
            }
    }

    set commonNutrition(commonNutrition: string) {
        (this.dependency.details as IndependentTracker).templateDetails = new FoodTrackerFormTemplate();
        ((this.dependency.details as IndependentTracker).templateDetails as FoodTrackerFormTemplate)
            .commonNutrition = commonNutrition;
    }

    set pushFormValues(values: {max: number, min: number, move: number}) {
        ((this.dependency.details as IndependentTracker).templateDetails as PushTrackerFormTemplate) = 
            {
                type: TrackerAddFormTemplate.PUSH,
                maximum: values.max,
                minimum: values.min,
                move: values.move
            }
    } 

    set rangeFormValues(values: {max: number, min: number, move: number}) {
        ((this.dependency.details as IndependentTracker).templateDetails as PushTrackerFormTemplate) = 
            {
                type: TrackerAddFormTemplate.PUSH,
                maximum: values.max,
                minimum: values.min,
                move: values.move
            }
    } 

    set selectedTemplate(template: TrackerAddFormTemplate) {
        (this.dependency.details as IndependentTracker).selectedTemplate = template;
    }

}

export interface ITrackerChartData {
    backgroundColor: string
    backgroundImage?: string
    seriesColor: string
    type: ChartType;
    typelabel: string;
}

export interface IDependencyType {
    selectedType: TrackerDependencyType;
    details?: DependentTracker | IndependentTracker;
}

export class DependentTracker {
    readonly type: TrackerDependencyType.FORMULA = TrackerDependencyType.FORMULA;
    formula: Partial<Formula>;
    dependsOnTrackers: string[];
}

export class IndependentTracker {
    readonly type: TrackerDependencyType.INDEPENDENT = TrackerDependencyType.INDEPENDENT;
    selectedTemplate: TrackerAddFormTemplate;
    templateDetails?: trackerFormTemplateType;
}


export type TrackingRightUserType = "both" | typeof UserRole.SUPER_ADMIN | typeof UserRole.USER;